import './App.css';
import { useState, useRef, useEffect, useMemo } from 'react';
import rosters from './rosters.json';
import { supabase } from './supabaseClient';

function App() {
    // College leaderboard filter state
    const [collegeLeaderboardFilter, setCollegeLeaderboardFilter] = useState('all');
  const [result, setResult] = useState('');
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0); /* milliseconds */
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [inputs, setInputs] = useState(['', '', '', '', '']);
  const [validations, setValidations] = useState([null, null, null, null, null]); /* null, true (correct), or false (incorrect) */
  const [positions] = useState(['QB', 'RB', 'WR', 'WR', 'TE']);
  const [openDropdown, setOpenDropdown] = useState(null); /* which dropdown is open */
  const [filteredPlayers, setFilteredPlayers] = useState([[], [], [], [], []]); /* filtered suggestions for each input */
  const [highlightedIndex, setHighlightedIndex] = useState(-1); /* which suggestion is highlighted */
  const [showResultsPopup, setShowResultsPopup] = useState(false); /* show results popup */
  const [completedResult, setCompletedResult] = useState(null); /* store completed result data */
  const [showNameModal, setShowNameModal] = useState(false); /* show name input modal */
  const [playerName, setPlayerName] = useState(''); /* player name for score submission */
  const [submittedScores, setSubmittedScores] = useState([]); /* store submitted scores */
  const [showDirectionsModal, setShowDirectionsModal] = useState(false); /* show directions modal */
  const [currentPage, setCurrentPage] = useState('game'); /* 'game' or 'leaderboard' */
  const [gameMode, setGameMode] = useState('nfl'); /* 'nfl' or 'college' */
  
  /* College game state */
  const [collegeResult, setCollegeResult] = useState('');
  const [collegeRunning, setCollegeRunning] = useState(false);
  const [collegeElapsed, setCollegeElapsed] = useState(0);
  const [selectedCollege, setSelectedCollege] = useState('');
  const [collegeInputs, setCollegeInputs] = useState(['', '', '', '', '']);
  const [collegeValidations, setCollegeValidations] = useState([null, null, null, null, null]);
  const [collegeOpenDropdown, setCollegeOpenDropdown] = useState(null);
  const [collegeFilteredPlayers, setCollegeFilteredPlayers] = useState([[], [], [], [], []]);
  const [collegeHighlightedIndex, setCollegeHighlightedIndex] = useState(-1);
  const [showCollegeResultsPopup, setShowCollegeResultsPopup] = useState(false);
  const [collegeCompletedResult, setCollegeCompletedResult] = useState(null);
  const [collegeSubmittedScores, setCollegeSubmittedScores] = useState([]);
  const [showCollegeDirectionsModal, setShowCollegeDirectionsModal] = useState(false); /* show college directions modal */
  
  const intervalRef = useRef(null);
  const collegeIntervalRef = useRef(null);
  const startRef = useRef(0);
  const collegeStartRef = useRef(0);
  const dropdownRefs = useRef([null, null, null, null, null]);
  const collegeDropdownRefs = useRef([null, null, null, null, null]);

  /* Get all distinct players from rosters */
  const allPlayers = useMemo(() => {
    const players = new Set();
    for (const filename of Object.keys(rosters)) {
      const data = rosters[filename];
      const playerList = data.G || [];
      playerList.forEach(p => {
        if (p && p.trim()) {
          players.add(p);
        }
      });
    }
    return Array.from(players).sort();
  }, []);

  /* Get all valid colleges (those with at least 1 QB, 1 RB, 2 WR, 1 TE) */
  const validColleges = useMemo(() => {
    const collegeMap = new Map(); /* college -> { QB: [], RB: [], WR: [], TE: [] } */
    
    for (const filename of Object.keys(rosters)) {
      const data = rosters[filename];
      const collegesRaw = data.M || [];
      const positions = data.C || [];
      const players = data.G || [];
      
      for (let i = 0; i < collegesRaw.length; i++) {
        const collegesStr = collegesRaw[i];
        if (!collegesStr || !collegesStr.trim()) continue;
        
        /* Split by semicolon to handle multiple colleges */
        const collegesList = collegesStr.split(';').map(c => c.trim()).filter(c => c);
        
        const position = positions[i];
        const player = players[i];
        
        /* Add player to each college they attended */
        for (const college of collegesList) {
          if (!collegeMap.has(college)) {
            collegeMap.set(college, { QB: [], RB: [], WR: [], TE: [] });
          }
          
          if (position && player) {
            const posArray = collegeMap.get(college)[position];
            if (posArray && !posArray.includes(player)) {
              posArray.push(player);
            }
          }
        }
      }
    }
    
    // Filter to only colleges with required positions
    const valid = [];
    for (const [college, posPlayers] of collegeMap) {
      if (
        posPlayers.QB.length >= 1 &&
        posPlayers.RB.length >= 1 &&
        posPlayers.WR.length >= 2 &&
        posPlayers.TE.length >= 1
      ) {
        valid.push(college);
      }
    }
    
    return valid.sort();
  }, []);

  function getRandomInt(max) {
    return Math.floor(Math.random() * Math.floor(max));
  }

  function findMatchingPlayers(year, team, position) {
    // Search through all rosters to find players matching year, team, and position
    const matching = [];
    
    for (const filename of Object.keys(rosters)) {
      const data = rosters[filename];
      const years = data.A || [];
      const teams = data.B || [];
      const positions = data.C || [];
      const players = data.G || [];
      
      for (let i = 0; i < years.length; i++) {
        if (years[i] === year && teams[i] === team && positions[i] === position) {
          matching.push(players[i]);
        }
      }
    }
    
    return matching;
  }

  function findMatchingPlayersByCollege(college, position) {
    // Search through all rosters to find players matching college and position
    const matching = [];
    
    for (const filename of Object.keys(rosters)) {
      const data = rosters[filename];
      const collegesRaw = data.M || [];
      const positions = data.C || [];
      const players = data.G || [];
      
      for (let i = 0; i < collegesRaw.length; i++) {
        // Split by semicolon to handle multiple colleges
        const collegesList = collegesRaw[i].split(';').map(c => c.trim()).filter(c => c);
        
        if (collegesList.includes(college) && positions[i] === position) {
          if (!matching.includes(players[i])) {
            matching.push(players[i]);
          }
        }
      }
    }
    
    return matching;
  }

  function validateInput(inputIndex, value) {
    if (!selectedYear || !selectedTeam || value.trim() === '') {
      return null; // not yet validated
    }
    
    const matchingPlayers = findMatchingPlayers(selectedYear, selectedTeam, positions[inputIndex]);
    const isValid = matchingPlayers.some(p => p.toLowerCase() === value.toLowerCase());
    
    return isValid;
  }

  function validateCollegeInput(inputIndex, value) {
    if (!selectedCollege || value.trim() === '') {
      return null; // not yet validated
    }
    
    const matchingPlayers = findMatchingPlayersByCollege(selectedCollege, positions[inputIndex]);
    const isValid = matchingPlayers.some(p => p.toLowerCase() === value.toLowerCase());
    
    return isValid;
  }

  function handleInputChange(index, value) {
    const newInputs = [...inputs];
    newInputs[index] = value;
    setInputs(newInputs);
    
    const newValidations = [...validations];
    newValidations[index] = validateInput(index, value);
    setValidations(newValidations);
    
    // Filter suggestions based on input
    const newFiltered = [...filteredPlayers];
    if (value.trim() === '') {
      newFiltered[index] = [];
      setOpenDropdown(null);
    } else {
      newFiltered[index] = allPlayers.filter(player =>
        player.toLowerCase().includes(value.toLowerCase())
      );
      // Only open dropdown if input is not correct
      if (newValidations[index] !== true) {
        setOpenDropdown(index);
      } else {
        setOpenDropdown(null);
      }
    }
    setFilteredPlayers(newFiltered);
    setHighlightedIndex(-1); // reset highlighted index when filtering
  }

  function handleSelectPlayer(index, player) {
    const newInputs = [...inputs];
    newInputs[index] = player;
    setInputs(newInputs);
    
    const newValidations = [...validations];
    newValidations[index] = validateInput(index, player);
    setValidations(newValidations);
    
    setOpenDropdown(null);
    setFilteredPlayers([[], [], [], [], []]);
    setHighlightedIndex(-1);
  }

  function handleKeyDown(e, index) {
    if (openDropdown !== index || !filteredPlayers[index] || filteredPlayers[index].length === 0) {
      return;
    }

    const maxIndex = Math.min(filteredPlayers[index].length - 1, 9); // max 10 items shown

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const newIndex = prev < maxIndex ? prev + 1 : maxIndex;
          // Scroll into view after state update
          setTimeout(() => {
            const item = dropdownRefs.current[index]?.children[newIndex];
            if (item) {
              item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : 0;
          // Scroll into view after state update
          setTimeout(() => {
            const item = dropdownRefs.current[index]?.children[newIndex];
            if (item) {
              item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredPlayers[index].length) {
          handleSelectPlayer(index, filteredPlayers[index][highlightedIndex]);
          setHighlightedIndex(-1);
        }
        break;
      case 'Escape':
        setOpenDropdown(null);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  }

  function handleStart() {
    // Show directions modal without starting the game yet
    setShowDirectionsModal(true);
  }

  function handleStartGame() {
    // Close directions modal and start the game
    setShowDirectionsModal(false);
    
    if (running) return; // don't change while running
    const files = Object.keys(rosters);
    if (files.length === 0) return;
    const file = files[getRandomInt(files.length)];
    const data = rosters[file] || {};
    const aArr = data.A || [];
    const bArr = data.B || [];
    if (aArr.length === 0 || bArr.length === 0) {
      setResult('No data');
      return;
    }
    const a = aArr[getRandomInt(aArr.length)];
    const b = bArr[getRandomInt(bArr.length)];
    
    setSelectedYear(a);
    setSelectedTeam(b);
    setResult(`${a}, ${b}`);
    
    // Reset inputs and validations
    setInputs(['', '', '', '', '']);
    setValidations([null, null, null, null, null]);
    setOpenDropdown(null);
    setFilteredPlayers([[], [], [], [], []]);
    setHighlightedIndex(-1);

    // reset & start timer
    setElapsed(0);
    setRunning(true);
    startRef.current = Date.now();
    intervalRef.current = setInterval(() => {
      setElapsed(Date.now() - startRef.current);
    }, 100);
  }

  function handleCloseDirectionsModal() {
    setShowDirectionsModal(false);
  }

  function handleCloseCollegeDirectionsModal() {
    setShowCollegeDirectionsModal(false);
  }

  function handleReset() {
    // Reset timer and clear inputs
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setRunning(false);
    setElapsed(0);
    setResult('');
    setInputs(['', '', '', '', '']);
    setValidations([null, null, null, null, null]);
    setOpenDropdown(null);
    setFilteredPlayers([[], [], [], [], []]);
    setHighlightedIndex(-1);
  }

  function handleSubmit() {
    // Check if all inputs are correct (all true)
    const allCorrect = validations.every(v => v === true);
    if (!allCorrect) {
      return; // Don't submit if not all are correct
    }

    // Stop the timer
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setRunning(false);

    // Store result data and show popup
    setCompletedResult({
      team: selectedTeam,
      year: selectedYear,
      time: elapsed
    });
    setShowResultsPopup(true);
  }

  function closeResultsPopup() {
    setShowResultsPopup(false);
    setCompletedResult(null);
    setResult(''); // Hide the randomized result
    // Reset for next round - but don't start timer
    setInputs(['', '', '', '', '']);
    setValidations([null, null, null, null, null]);
    setOpenDropdown(null);
    setFilteredPlayers([[], [], [], [], []]);
    setHighlightedIndex(-1);
  }

  const bannedWords = [
    //
    'shit', 'fuck', 'bitch', 'faggot', 'fag', 'gay', 'pedophile', 'sex', 'ass', 'asshole', 
    'boner', 'blow job', 'suck', 'vagina', 'dick', 'penis', 'cum', 'nigger', 'tits', 'cock', 
    'clit', 'rape', 'fucking', 'slut', 'jizz', 'masterbate', 'nigga', 'orgasm', 'piss', 'poop', 
    'porn', 'pussy', 'raping', 'cunt'
  ];
  const [nameError, setNameError] = useState('');

  function handleSubmitScore() {
    setShowNameModal(true);
  }

  async function handleNameSubmit() {
    if (playerName.trim() === '') {
      return;
    }
    // Slur filter
    const lowerName = playerName.toLowerCase();
    if (bannedWords.some(word => lowerName.includes(word))) {
      setNameError('Please choose another name.');
      return;
    }
    setNameError('');
    // Store the score with player name and timestamp
    if (!completedResult || !completedResult.team || !completedResult.year || !completedResult.time) {
      setNameError('Game result is missing. Please try again.');
      return;
    }
    const newScore = {
      user_name: playerName,
      team: completedResult.team,
      season: completedResult.year ? `${completedResult.year}-${Number(completedResult.year) + 1}` : '',
      game_completed: completedResult.time,
      timestamp: new Date().toISOString()
    };
    // Insert into Supabase and log response
    const { data, error } = await supabase.from('nfl_roster_leaderboard').insert([newScore]);
    if (error) {
      console.error('Supabase insert error:', error);
      console.log('CompletedResult:', completedResult);
      console.log('Insert object:', newScore);
      setNameError('Failed to submit score. Please try again.');
      return;
    }
    console.log('Supabase insert success:', data);
    setShowNameModal(false);
    setPlayerName('');
    setShowResultsPopup(false);
    setCompletedResult(null);
    // Force leaderboard refetch before switching page
    await fetchNflLeaderboard();
    setCurrentPage('leaderboard');
  }


  function handleBackToGame() {
    setCurrentPage('game');
    setShowResultsPopup(false);
    setShowCollegeResultsPopup(false);
    setCompletedResult(null);
    setCollegeCompletedResult(null);
    if (gameMode === 'nfl') {
      closeResultsPopup();
      setRunning(false);
      setResult('');
      setElapsed(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } else {
      closeCollegeResultsPopup();
      setCollegeRunning(false);
      setCollegeResult('');
      setCollegeElapsed(0);
      if (collegeIntervalRef.current) {
        clearInterval(collegeIntervalRef.current);
        collegeIntervalRef.current = null;
      }
    }
  }

  function handleCollegeStart() {
    // Show directions modal without starting the game
    setShowCollegeDirectionsModal(true);
  }

  function handleStartCollegeGame() {
    // Close directions modal and start the game
    setShowCollegeDirectionsModal(false);
    
    if (collegeRunning) return;
    if (validColleges.length === 0) {
      setCollegeResult('No valid colleges');
      return;
    }
    
    const college = validColleges[getRandomInt(validColleges.length)];
    
    setSelectedCollege(college);
    setCollegeResult(college);
    
    // Reset inputs and validations
    setCollegeInputs(['', '', '', '', '']);
    setCollegeValidations([null, null, null, null, null]);
    setCollegeOpenDropdown(null);
    setCollegeFilteredPlayers([[], [], [], [], []]);
    setCollegeHighlightedIndex(-1);

    // reset & start timer
    setCollegeElapsed(0);
    setCollegeRunning(true);
    collegeStartRef.current = Date.now();
    collegeIntervalRef.current = setInterval(() => {
      setCollegeElapsed(Date.now() - collegeStartRef.current);
    }, 100);
  }

  function handleCollegeReset() {
    if (collegeIntervalRef.current) {
      clearInterval(collegeIntervalRef.current);
      collegeIntervalRef.current = null;
    }
    setCollegeRunning(false);
    setCollegeElapsed(0);
    setCollegeResult('');
    setCollegeInputs(['', '', '', '', '']);
    setCollegeValidations([null, null, null, null, null]);
    setCollegeOpenDropdown(null);
    setCollegeFilteredPlayers([[], [], [], [], []]);
    setCollegeHighlightedIndex(-1);
  }

  function handleCollegeInputChange(index, value) {
    const newInputs = [...collegeInputs];
    newInputs[index] = value;
    setCollegeInputs(newInputs);
    
    const newValidations = [...collegeValidations];
    newValidations[index] = validateCollegeInput(index, value);
    setCollegeValidations(newValidations);
    
    // Filter suggestions based on input
    const newFiltered = [...collegeFilteredPlayers];
    if (value.trim() === '') {
      newFiltered[index] = [];
      setCollegeOpenDropdown(null);
    } else {
      newFiltered[index] = allPlayers.filter(player =>
        player.toLowerCase().includes(value.toLowerCase())
      );
      if (newValidations[index] !== true) {
        setCollegeOpenDropdown(index);
      } else {
        setCollegeOpenDropdown(null);
      }
    }
    setCollegeFilteredPlayers(newFiltered);
    setCollegeHighlightedIndex(-1);
  }

  function handleCollegeSelectPlayer(index, player) {
    const newInputs = [...collegeInputs];
    newInputs[index] = player;
    setCollegeInputs(newInputs);
    
    const newValidations = [...collegeValidations];
    newValidations[index] = validateCollegeInput(index, player);
    setCollegeValidations(newValidations);
    
    setCollegeOpenDropdown(null);
    setCollegeFilteredPlayers([[], [], [], [], []]);
    setCollegeHighlightedIndex(-1);
  }

  function handleCollegeKeyDown(e, index) {
    if (collegeOpenDropdown !== index || !collegeFilteredPlayers[index] || collegeFilteredPlayers[index].length === 0) {
      return;
    }

    const maxIndex = Math.min(collegeFilteredPlayers[index].length - 1, 9);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setCollegeHighlightedIndex(prev => {
          const newIndex = prev < maxIndex ? prev + 1 : maxIndex;
          setTimeout(() => {
            const item = collegeDropdownRefs.current[index]?.children[newIndex];
            if (item) {
              item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setCollegeHighlightedIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : 0;
          setTimeout(() => {
            const item = collegeDropdownRefs.current[index]?.children[newIndex];
            if (item) {
              item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (collegeHighlightedIndex >= 0 && collegeHighlightedIndex < collegeFilteredPlayers[index].length) {
          handleCollegeSelectPlayer(index, collegeFilteredPlayers[index][collegeHighlightedIndex]);
          setCollegeHighlightedIndex(-1);
        }
        break;
      case 'Escape':
        setCollegeOpenDropdown(null);
        setCollegeHighlightedIndex(-1);
        break;
      default:
        break;
    }
  }

  function handleCollegeSubmit() {
    const allCorrect = collegeValidations.every(v => v === true);
    if (!allCorrect) {
      return;
    }

    if (collegeIntervalRef.current) {
      clearInterval(collegeIntervalRef.current);
      collegeIntervalRef.current = null;
    }
    setCollegeRunning(false);

    setCollegeCompletedResult({
      college: selectedCollege,
      time: collegeElapsed
    });
    setShowCollegeResultsPopup(true);
  }

  function closeCollegeResultsPopup() {
    setShowCollegeResultsPopup(false);
    setCollegeCompletedResult(null);
    setCollegeResult(''); // Hide the randomized college result
    setCollegeInputs(['', '', '', '', '']);
    setCollegeValidations([null, null, null, null, null]);
    setCollegeOpenDropdown(null);
    setCollegeFilteredPlayers([[], [], [], [], []]);
    setCollegeHighlightedIndex(-1);
  }

  async function handleCollegeNameSubmit() {
    if (playerName.trim() === '') {
      return;
    }
    // Slur filter
    const lowerName = playerName.toLowerCase();
    if (bannedWords.some(word => lowerName.includes(word))) {
      setNameError('Inappropriate name. Please choose another.');
      return;
    }
    setNameError('');
    console.log('handleCollegeNameSubmit called');
    console.log('collegeCompletedResult:', collegeCompletedResult);
    if (!collegeCompletedResult || !collegeCompletedResult.college || !collegeCompletedResult.time) {
      setNameError('Game result is missing. Please try again.');
      return;
    }
    const newScore = {
      user_name: playerName,
      college: collegeCompletedResult.college,
      game_completed: collegeCompletedResult.time,
      timestamp: new Date().toISOString()
    };
    // Insert into Supabase and log response
    const { data, error } = await supabase.from('college_roster_leaderboard').insert([newScore]);
    if (error) {
      console.error('Supabase insert error:', error);
      console.log('Insert object:', newScore);
      setNameError('Failed to submit score. Please try again.');
      return;
    }
    console.log('Supabase insert success:', data);
    // Refetch leaderboard before switching page
    await fetchCollegeLeaderboard();
    setShowNameModal(false);
    setPlayerName('');
    setShowCollegeResultsPopup(false);
    setCollegeCompletedResult(null);
    setCurrentPage('leaderboard');
  }

  const [nflLeaderboard, setNflLeaderboard] = useState([]);
  const [collegeLeaderboard, setCollegeLeaderboard] = useState([]);
  const [leaderboardFilter, setLeaderboardFilter] = useState('all'); // 'week', 'month', 'all'

  // Helper to get start date for filter
  function getFilterStartDate(filter) {
    const now = new Date();
    if (filter === 'week') {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Monday as start
      return new Date(now.setDate(diff));
    } else if (filter === 'month') {
      return new Date(now.getFullYear(), now.getMonth(), 1);
    }
    return null;
  }

  // Fetch NFL leaderboard
  async function fetchNflLeaderboard() {
    let query = supabase
      .from('nfl_roster_leaderboard')
      .select('*')
      .order('game_completed', { ascending: true });
    const startDate = getFilterStartDate(leaderboardFilter);
    if (startDate) {
      query = query.gte('timestamp', startDate.toISOString());
    }
    const { data, error } = await query;
    if (!error && data) {
      console.log('Fetched NFL leaderboard:', data);
      setNflLeaderboard(data);
    } else if (error) {
      console.error('Error fetching NFL leaderboard:', error);
    }
  }
  useEffect(() => {
    fetchNflLeaderboard();
  }, [leaderboardFilter, currentPage]);

  async function fetchCollegeLeaderboard() {
    let query = supabase
      .from('college_roster_leaderboard')
      .select('*')
      .order('game_completed', { ascending: true });
    const startDate = getFilterStartDate(leaderboardFilter);
    if (startDate) {
      query = query.gte('timestamp', startDate.toISOString());
    }
    const { data, error } = await query;
    if (!error && data) {
      setCollegeLeaderboard(data);
    }
  }
  useEffect(() => {
    fetchCollegeLeaderboard();
  }, [leaderboardFilter, collegeLeaderboardFilter]);

  // College leaderboard filter is separate for UI

  // Auto-submit NFL game when all inputs become correct
  useEffect(() => {
    if (validations.length > 0 && validations.every(v => v === true) && !showResultsPopup) {
      handleSubmit();
    }
  }, [validations, showResultsPopup]);

  // Auto-submit College game when all inputs become correct
  useEffect(() => {
    if (collegeValidations.length > 0 && collegeValidations.every(v => v === true) && !showCollegeResultsPopup) {
      handleCollegeSubmit();
    }
  }, [collegeValidations, showCollegeResultsPopup]);

  // Load submitted scores from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem('nflScores');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setSubmittedScores(parsed);
      }
      const collegRaw = localStorage.getItem('collegeScores');
      if (collegRaw) {
        const collegeParsed = JSON.parse(collegRaw);
        if (Array.isArray(collegeParsed)) setCollegeSubmittedScores(collegeParsed);
      }
    } catch (e) {
      console.error('Failed to load saved scores', e);
    }
  }, []);

  // Persist submittedScores to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('nflScores', JSON.stringify(submittedScores));
      localStorage.setItem('collegeScores', JSON.stringify(collegeSubmittedScores));
    } catch (e) {
      console.error('Failed to save scores', e);
    }
  }, [submittedScores, collegeSubmittedScores]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (collegeIntervalRef.current) clearInterval(collegeIntervalRef.current);
    };
  }, []);

  function formatElapsed(ms) {
    const s = Math.floor(ms / 1000);
    const cs = Math.floor((ms % 1000) / 10); // centiseconds
    return `${s}.${cs.toString().padStart(2,'0')}s`;
  }

  return (
    <div className="App">
      <div className="top-section">
        <h1 className="top-title">
          {currentPage === 'leaderboard' ? '🏈 NFL Ball Knowledge Leaderboard 🏈' : '🏈 NFL Ball Knowledge 🏈'}
        </h1>
        {currentPage === 'game' && (
          <button 
            type="button" 
            className="leaderboard-nav-button"
            onClick={() => setCurrentPage('leaderboard')}
          >
            Leaderboard
          </button>
        )}
        {currentPage === 'leaderboard' && (
          <button 
            type="button" 
            className="back-button"
            onClick={handleBackToGame}
          >
            Back to Game
          </button>
        )}
      </div>
      {currentPage === 'game' && (
        <div className="game-mode-selector">
          <button 
            type="button" 
            className={`mode-button ${gameMode === 'nfl' ? 'active' : ''}`}
            onClick={() => setGameMode('nfl')}
          >
            NFL Roster Quiz
          </button>
          <button 
            type="button" 
            className={`mode-button ${gameMode === 'college' ? 'active' : ''}`}
            onClick={() => setGameMode('college')}
          >
            College to NFL Quiz
          </button>
        </div>
      )}
      <header className="App-header">
        {currentPage === 'game' ? (
          <>
            {gameMode === 'nfl' ? (
              <>
                {/*<img src={logo} className="App-logo" alt="logo" /> */}
                {result && (() => {
                  const [year, team] = result.split(',').map(s => s.trim());
                  const formattedYear = year && !isNaN(year) ? `${year}-${Number(year) + 1}` : year;
                  return <div className="result-display">{formattedYear}, {team}</div>;
                })()}
                {!running && (
                  <button type="button" className="start-button" onClick={handleStart} disabled={running}>Start</button>
                )}
                {running && (
                  <>
                    <p className="lineup-prompt">Enter Players Below</p>
                    <div className="lineup-container">
                      {positions.map((pos, index) => (
                        <div key={index} className="input-wrapper">
                          <input
                            className={`lineup-input ${validations[index] === true ? 'correct' : validations[index] === false ? 'incorrect' : ''}`}
                            type="text"
                            placeholder={pos}
                            value={inputs[index]}
                            onChange={(e) => handleInputChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                            onFocus={() => inputs[index] && setOpenDropdown(index)}
                          />
                          {validations[index] === true && <span className="checkmark">✓</span>}
                          {openDropdown === index && filteredPlayers[index] && filteredPlayers[index].length > 0 && (
                            <div className="autocomplete-dropdown" ref={el => dropdownRefs.current[index] = el}>
                              {filteredPlayers[index].slice(0, 10).map((player, itemIndex) => (
                                <div
                                  key={player}
                                  className={`autocomplete-item ${itemIndex === highlightedIndex ? 'highlighted' : ''}`}
                                  onClick={() => handleSelectPlayer(index, player)}
                                >
                                  {player}
                                </div>
                              ))}
                              {filteredPlayers[index].length > 10 && (
                                <div className="autocomplete-more">
                                  +{filteredPlayers[index].length - 10} more
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <button type="button" className="stop-button" onClick={handleReset}>Reset</button>
                    <div className="timer-display">{formatElapsed(elapsed)}</div>
                  </>
                )}

            {showResultsPopup && completedResult && (
              <div className="results-modal-overlay" onClick={closeResultsPopup}>
                <div className="results-modal" onClick={(e) => e.stopPropagation()}>
                  <h2>Completed!</h2>
                  <div className="results-content">
                    <div className="result-item">
                      <span className="result-label">Team & Year:</span>
                      <span className="result-value">{completedResult.year}, {completedResult.team}</span>
                    </div>
                    <div className="result-item">
                      <span className="result-label">Time Elapsed:</span>
                      <span className="result-value">{formatElapsed(completedResult.time)}</span>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="close-popup-button"
                    onClick={closeResultsPopup}
                  >
                    New Game
                  </button>
                  <button 
                    type="button" 
                    className="submit-score-button"
                    onClick={handleSubmitScore}
                  >
                    Submit Score
                  </button>
                </div>
              </div>
            )}

            {showDirectionsModal && (
              <div className="directions-modal-overlay" onClick={handleCloseDirectionsModal}>
                <div className="directions-modal" onClick={(e) => e.stopPropagation()}>
                  <h3>NFL Roster Quiz</h3>
                  <p className="directions-text">
                    An NFL team and season will be provided. Enter 1 QB, 1 RB, 2 WR, 1 TE in the shortest amount of time in the slots provided.
                    Once finished, you will be ranked against other players. Good Luck!
                  </p>
                  <button
                    type="button"
                    className="directions-start-button"
                    onClick={handleStartGame}
                  >
                    Start
                  </button>
                </div>
              </div>
            )}

            {showNameModal && (
              <div className="name-modal-overlay" onClick={() => setShowNameModal(false)}>
                <div className="name-modal" onClick={(e) => e.stopPropagation()}>
                  <h2>Submit Score</h2>
                  <p className="name-modal-label">Enter your name:</p>
                  <input
                    type="text"
                    className="name-input"
                    placeholder="Your name"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleNameSubmit();
                      }
                    }}
                    autoFocus
                  />
                  {nameError && (
                    <div className="name-error">{nameError}</div>
                  )}
                  <div className="name-modal-buttons">
                    <button
                      type="button"
                      className="name-modal-submit"
                      onClick={handleNameSubmit}
                      disabled={playerName.trim() === ''}
                    >
                      Submit
                    </button>
                    <button
                      type="button"
                      className="name-modal-cancel"
                      onClick={() => setShowNameModal(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
            </>
            ) : (

              <>
                {!collegeRunning && (
                  <button type="button" className="start-button" onClick={handleCollegeStart} disabled={collegeRunning}>Start</button>
                )}
                {collegeResult && <div className="result-display">{collegeResult}</div>}
                {collegeRunning && (
                  <>
                    <p className="lineup-prompt">Enter Players Below</p>
                    <div className="lineup-container">
                      {positions.map((pos, index) => (
                        <div key={index} className="input-wrapper">
                          <input
                            className={`lineup-input ${collegeValidations[index] === true ? 'correct' : collegeValidations[index] === false ? 'incorrect' : ''}`}
                            type="text"
                            placeholder={pos}
                            value={collegeInputs[index]}
                            onChange={(e) => handleCollegeInputChange(index, e.target.value)}
                            onKeyDown={(e) => handleCollegeKeyDown(e, index)}
                            onFocus={() => collegeInputs[index] && setCollegeOpenDropdown(index)}
                          />
                          {collegeValidations[index] === true && <span className="checkmark">✓</span>}
                          {collegeOpenDropdown === index && collegeFilteredPlayers[index] && collegeFilteredPlayers[index].length > 0 && (
                            <div className="autocomplete-dropdown" ref={el => collegeDropdownRefs.current[index] = el}>
                              {collegeFilteredPlayers[index].slice(0, 10).map((player, itemIndex) => (
                                <div
                                  key={player}
                                  className={`autocomplete-item ${itemIndex === collegeHighlightedIndex ? 'highlighted' : ''}`}
                                  onClick={() => handleCollegeSelectPlayer(index, player)}
                                >
                                  {player}
                                </div>
                              ))}
                              {collegeFilteredPlayers[index].length > 10 && (
                                <div className="autocomplete-more">
                                  +{collegeFilteredPlayers[index].length - 10} more
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    <button type="button" className="stop-button" onClick={handleCollegeReset}>Reset</button>
                    <div className="timer-display">{formatElapsed(collegeElapsed)}</div>
                  </>
                )}

                {showCollegeResultsPopup && collegeCompletedResult && (
                  <div className="results-modal-overlay" onClick={closeCollegeResultsPopup}>
                    <div className="results-modal" onClick={(e) => e.stopPropagation()}>
                      <h2>Completed!</h2>
                      <div className="results-content">
                        <div className="result-item">
                          <span className="result-label">College:</span>
                          <span className="result-value">{collegeCompletedResult.college}</span>
                        </div>
                        <div className="result-item">
                          <span className="result-label">Time Elapsed:</span>
                          <span className="result-value">{formatElapsed(collegeCompletedResult.time)}</span>
                        </div>
                      </div>
                      <button 
                        type="button" 
                        className="close-popup-button"
                        onClick={closeCollegeResultsPopup}
                      >
                        New Game
                      </button>
                      <button 
                        type="button" 
                        className="submit-score-button"
                        onClick={() => setShowNameModal(true)}
                      >
                        Submit Score
                      </button>
                    </div>
                  </div>
                )}

                {showNameModal && (
                  <div className="name-modal-overlay" onClick={() => setShowNameModal(false)}>
                    <div className="name-modal" onClick={(e) => e.stopPropagation()}>
                      <h2>Submit Score</h2>
                      <p className="name-modal-label">Enter your name:</p>
                      <input
                        type="text"
                        className="name-input"
                        placeholder="Your name"
                        value={playerName}
                        onChange={(e) => setPlayerName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handleCollegeNameSubmit();
                          }
                        }}
                        autoFocus
                      />
                      {nameError && (
                        <div className="name-error">{nameError}</div>
                      )}
                      <div className="name-modal-buttons">
                        <button
                          type="button"
                          className="name-modal-submit"
                          onClick={handleCollegeNameSubmit}
                          disabled={playerName.trim() === ''}
                        >
                          Submit
                        </button>
                        <button
                          type="button"
                          className="name-modal-cancel"
                          onClick={() => setShowNameModal(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {showCollegeDirectionsModal && (
                  <div className="directions-modal-overlay" onClick={handleCloseCollegeDirectionsModal}>
                    <div className="directions-modal" onClick={(e) => e.stopPropagation()}>
                      <h3>College to NFL Quiz</h3>
                        <p className="directions-text">
                        A College will be generated. Enter NFL Players (2010-Current) that attended the college and plays the position in the slots provided. Once finished, you will be ranked against other players. Good Luck!
                      </p>
                      <button
                        type="button"
                        className="directions-start-button"
                        onClick={handleStartCollegeGame}
                      >
                        Start
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <div className="leaderboard-mode-selector">
              <button 
                type="button" 
                className={`mode-button ${gameMode === 'nfl' ? 'active' : ''}`}
                onClick={() => setGameMode('nfl')}
              >
                NFL Roster Quiz
              </button>
              <button 
                type="button" 
                className={`mode-button ${gameMode === 'college' ? 'active' : ''}`}
                onClick={() => setGameMode('college')}
              >
                College to NFL Quiz
              </button>
            </div>
            <div className="leaderboard-page">
              <div className="leaderboard-content">
                {gameMode === 'nfl' ? (
                  (() => {
                    const filteredScores = nflLeaderboard.filter(score => {
                      if (leaderboardFilter === 'all') return true;
                      const startDate = getFilterStartDate(leaderboardFilter);
                      return new Date(score.timestamp) >= startDate;
                    });
                    return (
                      <>
                        <div className="leaderboard-filter-buttons">
                          <button onClick={() => setLeaderboardFilter('week')} className={leaderboardFilter === 'week' ? 'active' : ''}>Weekly Leader</button>
                          <button onClick={() => setLeaderboardFilter('month')} className={leaderboardFilter === 'month' ? 'active' : ''}>Month to Date</button>
                          <button onClick={() => setLeaderboardFilter('all')} className={leaderboardFilter === 'all' ? 'active' : ''}>All Time</button>
                        </div>
                        {filteredScores.length === 0 ? (
                          <p className="no-scores">All scores are hidden until you submit your first quiz!</p>
                        ) : (
                          <>
                            <div className="leaderboard-column-headers">
                              <div className="column-rank">Rank</div>
                              <div className="column-name">Name</div>
                              <div className="column-year-team">Year, Team</div>
                              <div className="column-time">Time (s)</div>
                            </div>
                            <div className="leaderboard-rows">
                              {filteredScores.map((score, index) => (
                                <div key={index} className={`leaderboard-row ${index === 0 ? 'first-place' : ''}`}>
                                  <div className="column-rank">{index === 0 ? '👑' : index + 1}</div>
                                  <div className="column-name">{score.user_name}</div>
                                  <div className="column-year-team">{
                                    score.season && /^\d{4}-\d{4}$/.test(score.season)
                                      ? score.season
                                      : score.season
                                        ? `${score.season}-${Number(score.season) + 1}`
                                        : ''
                                  }, {score.team}</div>
                                  <div className="column-time">{formatElapsed(score.game_completed)}</div>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </>
                    );
                  })()
                ) : (
                  collegeLeaderboard.length === 0 ? (
                    <p className="no-scores">All scores are hidden until you submit your first quiz!</p>
                  ) : (
                    <>
                      <div className="leaderboard-filter-buttons">
                        <button onClick={() => setCollegeLeaderboardFilter('week')} className={collegeLeaderboardFilter === 'week' ? 'active' : ''}>Weekly Leader</button>
                        <button onClick={() => setCollegeLeaderboardFilter('month')} className={collegeLeaderboardFilter === 'month' ? 'active' : ''}>Month to Date</button>
                        <button onClick={() => setCollegeLeaderboardFilter('all')} className={collegeLeaderboardFilter === 'all' ? 'active' : ''}>All Time</button>
                      </div>
                      <div className="leaderboard-column-headers">
                        <div className="column-rank">Rank</div>
                        <div className="column-name">Name</div>
                        <div className="column-year-team college">College</div>
                        <div className="column-time">Time (s)</div>
                      </div>
                      <div className="leaderboard-rows">
                        {collegeLeaderboard
                          .filter(score => {
                            if (collegeLeaderboardFilter === 'all') return true;
                            const startDate = getFilterStartDate(collegeLeaderboardFilter);
                            return new Date(score.timestamp) >= startDate;
                          })
                          .map((score, index) => (
                          <div key={index} className={`leaderboard-row ${index === 0 ? 'first-place' : ''}`}>
                            <div className="column-rank">{index === 0 ? '👑' : index + 1}</div>
                            <div className="column-name">{score.user_name}</div>
                            <div className="column-year-team college">{score.college}</div>
                            <div className="column-time">{formatElapsed(score.game_completed)}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )
                )}
              </div>
            </div>
          </>
        )}
      </header>
    </div>
  );
}

export default App;
